import { useEffect, useRef, useCallback } from 'react';
import { fetchSessionInfo } from '../api/fetchSessionInfo';
import { captureException, toError } from '../../../../internal/ravenUtils';
import type { HubHttpClient } from '../../../HttpClientContext';

const POLLING_INTERVAL_MS = 1000;
export const MAX_POLLING_COUNT = 300;

export interface SessionPollingCallbacks {
  onPollTick: () => void;
  onPollSuccess: (params: Map<string, string>, status: string) => void;
  onPollError: () => void;
  onTimeout: () => void;
}

export interface UseSessionPollingOptions {
  portalId: number;
  sessionId: string;
  callbacks: SessionPollingCallbacks;
  enabled: boolean;
  /**
   * HTTP client override. Defaults to `hub-http/clients/apiClient`
   * (portal-session auth). Pass a custom client for non-portal contexts
   * (e.g. Janus IDP stacks via `HttpClientProvider`).
   */
  httpClient?: HubHttpClient;
}

export interface UseSessionPollingReturn {
  startPolling: () => void;
  stopPolling: () => void;
  isPolling: boolean;
  pollingCount: number;
}

export function useSessionPolling({
  portalId,
  sessionId,
  callbacks,
  enabled,
  httpClient,
}: UseSessionPollingOptions): UseSessionPollingReturn {
  const pollingTimeoutRef = useRef<number | undefined>(undefined);
  const pollingCountRef = useRef(0);
  const isPollingRef = useRef(false);
  const pollVersionRef = useRef(0);
  const callbacksRef = useRef(callbacks);

  useEffect(() => {
    callbacksRef.current = callbacks;
  }, [callbacks]);

  const stopPolling = useCallback(() => {
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = undefined;
    }
    isPollingRef.current = false;
    pollVersionRef.current += 1;
  }, []);

  const pollSessionInfo = useCallback(() => {
    if (!isPollingRef.current) {
      return;
    }

    const version = pollVersionRef.current;

    fetchSessionInfo(portalId, sessionId, httpClient)
      .then((response: { status: string; params: Map<string, string> }) => {
        if (!isPollingRef.current || pollVersionRef.current !== version) {
          return;
        }

        pollingCountRef.current += 1;
        callbacksRef.current.onPollTick();

        if (response.status === 'PENDING') {
          if (pollingCountRef.current >= MAX_POLLING_COUNT) {
            stopPolling();
            callbacksRef.current.onTimeout();
          } else {
            pollingTimeoutRef.current = window.setTimeout(
              // eslint-disable-next-line react-hooks/immutability
              pollSessionInfo,
              POLLING_INTERVAL_MS
            );
          }
        } else {
          stopPolling();
          callbacksRef.current.onPollSuccess(response.params, response.status);
        }
      })
      .catch((err: unknown) => {
        // An in-flight poll can reject after polling stopped, e.g. when the
        // popup's window-message path already completed the flow. That is not
        // a failure, so bail before reporting.
        if (!isPollingRef.current || pollVersionRef.current !== version) {
          return;
        }

        captureException(
          new Error('Auth session polling request failed', {
            cause: toError(err),
          })
        );

        stopPolling();
        callbacksRef.current.onPollError();
      });
  }, [portalId, sessionId, httpClient, stopPolling]);

  const startPolling = useCallback(() => {
    if (isPollingRef.current) {
      return;
    }

    pollingCountRef.current = 0;
    isPollingRef.current = true;
    pollSessionInfo();
  }, [pollSessionInfo]);

  useEffect(() => {
    if (enabled && !isPollingRef.current) {
      startPolling();
    } else if (!enabled) {
      stopPolling();
    }
  }, [enabled, startPolling, stopPolling]);

  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  // eslint-disable-next-line react-hooks/refs
  return {
    startPolling,
    stopPolling,
    // eslint-disable-next-line react-hooks/refs
    isPolling: isPollingRef.current,
    // eslint-disable-next-line react-hooks/refs
    pollingCount: pollingCountRef.current,
  };
}
